<template>
  <div class="container-fluid customPadding">
    <div v-if="!isSimplifyForm">
      <div id="navigationButtons">
        <b-button class="text text-dark" variant="link" @click="returnToMainTable()"
          >Surveys
        </b-button>
        <b-icon icon="chevron-right" />
        <span class="text text-primary">{{ eventName }}</span>
        <b-button class="no-sm-float-right text text-primary" variant="link" @click="dirtyCheck()">
          <b-icon icon="folder"></b-icon>
          View Surveys
        </b-button>
      </div>

      <div v-if="showSurvey && !loading" id="personInfo" class="mt-2">
        <person-info :info="information" :scopeData="personData" :survey="survey"></person-info>
      </div>
    </div>

    <div v-if="loading" class="text-center text-secondary my-2 mt-5">
      <b-spinner class="align-middle secondary"></b-spinner>
      <strong>Loading {{ isSimplifyForm ? 'Form' : 'Survey' }}...</strong>
    </div>
    <div v-if="showSurvey && !loading">
      <p class="requiredStar">
        {{ backendValidationErrors }}
      </p>
      <b-card class="mt-5" style="border-radius: 16px">
        <div v-if="isSimplifyForm" class="mt-0 pb-2 mb-4">
          <div v-if="isSimplifyForm" id="backButton" class="backButton">
            <b-button class="pl-0" variant="link" @click="goToPersonSurveys()"
              ><b-icon icon="arrow-left" scale="0.8" /> Back</b-button
            >
          </div>
          <b-row class="justify-content-between">
            <b-col class="titleStyle mt-3" cols="auto">
              {{ eventName }}
            </b-col>
          </b-row>
        </div>

        <person-question-generator
          ref="personQuestionGenerator"
          :answers="personQuestionModel['QuestionAndAnswers']"
          :childOfListQuestions="personQuestionModel['ChildOfListQuestions']"
          :dependencyMap="personQuestionModel['QuestionDependenciesMap']"
          :dependentQuestion="personQuestionModel['DependentQuestions']"
          :listAnswers="personQuestionModel['ListAnswers']"
          :lov="personQuestionModel['QuestionAndLovs']"
          :personId="personQuestionModel['surveys']['personId']"
          :requiredQuestions="personQuestionModel['RequiredQuestions']"
          :schema="personQuestionModel['Questions']"
          @staticPercentageValidation="percentageValidation($event)"
        >
        </person-question-generator>
        <div class="text-right">
          <export-form-to-pdf
            ref="exportToPdf"
            :childOfListQuestions="personQuestionModel['ChildOfListQuestions']"
            :event-id="eventId"
            :eventName="eventName"
            :investigatorName="investigatorName"
            :questions="personQuestionModel['Questions']"
            :siteId="$route.params.siteId"
            :studyId="$route.params.studyId"
            :eventId="$route.params.eventId"
          ></export-form-to-pdf>
        </div>
        <saving-buttons
          :id="'personSavingButtons'"
          :busy="busy"
          @dirtyCheck="dirtyCheck()"
          @save="save($event)"
        ></saving-buttons>
      </b-card>

      <cancel-warning-modal
        :id="'cancelPersonModal'"
        :busy="busy"
        :message="''"
        @cancel="cancel()"
        @handleOk="redirect()"
        @modalSaveDraft="modalSaveDraft()"
      ></cancel-warning-modal>
      <saving-modal
        :id="'personSavingModal'"
        :busy="busy"
        :errorMessage="errorMessage"
        :modalLoading="modalLoading"
        :modalSuccessMessage="modalSuccessMessage"
        :modalTitle="modalTitle"
        :saveResponseStatus="saveResponseStatus"
        @handleOk="handleOk"
      ></saving-modal>

      <form-preview-modal
        :siteId="$route.params.siteId"
        :studyId="$route.params.studyId"
        :eventName="eventName"
        @save="save($event)"
      ></form-preview-modal>
    </div>
  </div>
</template>
<script>
  import PersonInfo from './PersonInfo';
  import PersonQuestionGenerator from '../question-model/PersonQuestionGenerator';
  import SavingModal from '../../utility/SavingModal';
  import FormPreviewModal from '../../utility/FormPreviewModal';
  import CancelWarningModal from '../../utility/CancelWarningModal';
  import SavingButtons from '../../utility/SavingButtons';
  import ExportFormToPdf from '../ExportFormToPdf';
  import { mapActions, mapGetters, mapMutations } from 'vuex';

  export default {
    components: {
      PersonInfo,
      PersonQuestionGenerator,
      SavingModal,
      CancelWarningModal,
      SavingButtons,
      ExportFormToPdf,
      FormPreviewModal,
    },
    name: 'CreatePersonSurvey',
    metaInfo() {
      let name = this.isSimplifyForm ? this.eventName : this.eventName + ' - Create Survey';
      let metaTitle = this.$appName + name;
      return {
        title: metaTitle,
      };
    },
    data() {
      return {
        busy: false,
        staticPercentageIds: [],
        staticListIndexes: [],
        percentagesIndexes: [],
        personQuestionModel: [],
        loading: false,
        showSurvey: false,
        eventId: '',
        personId: '',
        surveyId: '',
        information: {},
        eventName: '',
        toastSuccessMessage: '',
        toastVariant: '',
        modalSuccessMessage: '',
        modalLoading: false,
        modalTitle: '',
        saveResponseStatus: null,
        pathToModal: '',
        investigatorName: '',
      };
    },
    watch: {
      $route() {
        this.setParameters();
        this.getPersonQuestionModel();
      },
    },
    computed: {
      ...mapGetters([
        'personQuestionFormData',
        'backendValidationErrors',
        'emptySurvey',
        'personValidations',
        'dirtyPersonSurvey',
        'errorMessage',
      ]),
      survey() {
        return this.personQuestionModel['surveys'];
      },
      personData() {
        return {
          eventId: this.eventId,
          surveyId: this.surveyId,
        };
      },
      isSimplifyForm() {
        return this.$route.name === 'form-create';
      },
    },
    created() {
      this.setParameters();
      this.getPersonQuestionModel();
      this.busy = false;
    },
    destroyed() {
      this.setBackEndValidationErrors('');
      this.personQuestionModel = [];
      this.$store.commit('personValidations', 'reset');
    },
    methods: {
      ...mapMutations({
        setBackEndValidationErrors: 'backendValidationErrors',
        setValidationTrigger: 'validationTrigger',
      }),
      ...mapActions(['saveQuestions', 'checkForEmptySurvey', 'handleError']),
      setParameters() {
        this.eventId = this.$route.params.eventId;
        this.personId = this.$route.params.personId;
        this.surveyId = this.$route.params.surveyId;
      },
      async getPersonQuestionModel() {
        this.loading = true;
        try {
          this.setParameters();
          let params;
          if (this.isSimplifyForm) {
            params = {
              studyId: this.$route.params.studyId,
              siteId: this.$route.params.siteId,
              eventId: this.$route.params.eventId,
              project: 'simplify',
            };
          } else {
            params = {
              eventId: this.eventId,
              surveyId: !(this.surveyId === null || this.surveyId === undefined)
                ? this.surveyId
                : '',
              project: 'simplify',
            };
          }

          let url = '/api/get-person-survey-questions';
          if (this.isSimplifyForm) {
            url = '/api/show-forms';
            params = {
              studyId: this.$route.params.studyId,
              siteId: this.$route.params.siteId,
              eventId: this.$route.params.eventId,
            };
          }
          let response = await axios.get(url, { params: params });
          let containerName = Object.keys(response.data['response'])[0];
          this.personQuestionModel = response.data['response'][containerName];
          if (this.isSimplifyForm) {
            this.personQuestionModel['QuestionAndAnswers'] = [];
            this.personQuestionModel['ListAnswers'] = [];
            this.personQuestionModel['Answers'] = [];
          }
          this.information = response.data['refreshDetails']['response'];
          this.eventName = this.information['eventName'];
          this.investigatorName = this.information['PI'];
          this.showSurvey = true;
        } catch (error) {
          await this.$store.dispatch('handleError', {
            vm: this,
            error: error,
            message: "Something went wrong while fetching the person's questions.",
          });
        }
        this.loading = false;
      },
      goToPersonSurveys() {
        if (this.isSimplifyForm) {
          this.$router.push({
            name: 'forms',
          });
        } else {
          this.$router.push({
            name: 'view-person-surveys',
            params: { eventId: this.eventId, personId: this.personId },
          });
        }
      },
      redirect() {
        if (this.pathToModal === 'surveys') {
          this.$router.push({ name: 'surveys' });
        } else {
          this.goToPersonSurveys();
        }
      },
      dirtyCheck() {
        if (this.dirtyPersonSurvey) {
          this.$bvModal.show('cancelPersonModal');
        } else {
          this.goToPersonSurveys();
        }
      },
      returnToMainTable() {
        this.pathToModal = 'surveys';
        if (this.dirtyPersonSurvey) {
          this.$bvModal.show('cancelPersonModal');
        } else {
          this.$router.push({ name: 'surveys' });
        }
      },
      handleOk() {
        if (this.saveResponseStatus == 200 || this.saveResponseStatus == 201) {
          this.goToPersonSurveys();
        } else {
          this.$bvModal.hide('personSavingModal');
        }
      },
      modalSaveDraft() {
        this.$bvModal.hide('cancelPersonModal');
        this.save('saveDraft');
      },
      cancel() {
        this.pathToModal = '';
      },
      async save(savingStatus) {
        this.setValidationTrigger('personValidations');
        await this.$store.dispatch('checkForEmptySurvey', {
          questionData: this.personQuestionFormData,
        });
        this.busy = true;
        setTimeout(() => {
          this.validateChildren(savingStatus);
        }, 200);
      },
      async validateChildren(savingStatus) {
        this.modalSuccessMessage = 'Your survey has been saved as Draft!';
        if (savingStatus === 'saveSubmit') {
          this.modalSuccessMessage = this.isSimplifyForm
            ? 'Your form has been submitted!'
            : 'Your survey has been submitted!';
          const check = await this.validationCheck();
          const checkPercentage = await this.checkPercentageValidation();
          if (!check || !checkPercentage) {
            this.busy = false;
            return;
          }
        }
        if (this.emptySurvey) {
          this.$bvToast.toast('Nothing to save, please check your answers.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
          this.busy = false;
          return;
        }
        let status = savingStatus === 'saveSubmit' ? 'Submitted' : 'Draft';
        this.modalTitle = 'Saving survey...';
        this.modalLoading = true;
        this.$bvModal.show('personSavingModal');
        let payload = {
          questionData: this.personQuestionFormData,
          surveys: this.personQuestionModel.surveys,
          containerId: this.personQuestionModel['ContainerId'],
          protocolId: this.$store.getters.protocol.Id,
          status: status,
          isSimplifyForm: this.isSimplifyForm,
        };

        let response = await this.$store.dispatch('saveQuestions', payload);
        let statusCodes = [200, 201];
        if ('status' in response && statusCodes.includes(response.status)) {
          if (this.isSimplifyForm) {
            let documents = Object.keys(this.personQuestionFormData)
              .filter(el => el.includes('Documents'))
              .map(el => {
                return this.personQuestionFormData[el]['value'];
              });
            this.$refs.exportToPdf.exportPDF('sendEmail', documents);
          }
          this.saveResponseStatus = response.status;
          this.modalTitle = 'Saved!';
          this.modalLoading = false;
        } else {
          this.saveResponseStatus = response.status;
          this.modalTitle = 'Error!';
          this.modalLoading = false;
        }
        this.busy = false;
      },
      async validationCheck() {
        if (Object.values(this.personValidations).includes(false)) {
          this.$bvToast.toast(`Please fill all required fields`, {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
          return false;
        } else {
          return true;
        }
      },
      percentageValidation(staticList) {
        if (staticList.status === 'enable') {
          this.staticPercentageIds.push(staticList.id);
          this.staticPercentageIds = [...new Set(this.staticPercentageIds)];
        } else {
          let filtered = this.staticPercentageIds.filter(function(value) {
            return value !== staticList.id;
          });
          this.staticPercentageIds = [...new Set(filtered)];
        }
      },
      async checkPercentageValidation() {
        this.findIndexes();
        for (let i = 0; i < this.staticListIndexes.length; i++) {
          let person = await this.$refs.personQuestionGenerator.$children[
            this.staticListIndexes[i]
          ].$refs.observer.validate();
          this.$refs.personQuestionGenerator.$children[
            this.staticListIndexes[i]
          ].$children[0].$children[this.percentagesIndexes[i]]['percentageState'] = !!person;
        }
        for (let i = 0; i < this.staticListIndexes.length; i++) {
          let index = this.staticListIndexes[i];
          const isValid = await this.$refs.personQuestionGenerator.$children[
            index
          ].$refs.observer.validate();
          if (!isValid) {
            this.$bvToast.toast(`Please fill all required fields`, {
              title: 'Error',
              variant: 'danger',
              solid: true,
            });
            return false;
          }
        }
        return true;
      },
      findIndexes() {
        let staticPercentageIdsOrdered = [];
        this.staticListIndexes = [];
        this.percentagesIndexes = [];
        this.personQuestionModel['Questions'].forEach((element, index) => {
          if (this.staticPercentageIds.includes(element['Id'])) {
            this.staticListIndexes.push(index);
            staticPercentageIdsOrdered.push(element['Id']);
          }
        });
        staticPercentageIdsOrdered.forEach(element => {
          this.personQuestionModel['ChildOfListQuestions'][element].forEach(
            (childElement, index) => {
              if (childElement['Attribute_Type__c'] === 'Percentage') {
                this.percentagesIndexes.push(index);
              }
            },
          );
        });
      },
    },
  };
</script>
<style scoped>
  .backButton {
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0.208333px;
    color: #0000c9;
  }
  .titleStyle {
    font-weight: 600;
    font-size: 28px;
    line-height: 24px;
    color: #1c1c1c;
  }
</style>
